import { BusinessDayCalculatorPL, ClientRepositoryBase, collectionChangedJoinPointFilter, CommunityTransactionForPurchaseQuery, CompanyParametersQuery, CurrencyCollectionQuery, DocumentExchangeRateValidationBoPart, DocumentNumberBoPart, ExchangeRateMixin, fillDocumentContractorBasedOnContractorId, KsefNumberValidator, NumberingQuery, PaymentMethodQuery, propertyChangedFilter, propertyChangedJoinPointFilter, PurchaseKindQuery, rewriteDocumentContractorWithFreshDataAsync, rewriteExchangeRate, ToPayMixin, VatRateForPurchaseQuery, VatTableItem, VatTableItemContext, VatTableItemGrossRecalculation, VatTableItemNetRecalculation, VatTableItemRecalculationChangesProperty } from '@skylab-shared/business';
import { BO, BusinessObjectBase, CollectionChangedJoinPoint, CollectionChangingAction, PropertyChangedJoinPoint, QueryBus, Weaver } from '@skylab-shared/core';
import { CalculationMethod, DocumentPaymentListItemModel, ExchangeRateModel, PurchaseDocumentKind, PurchaseEInvoiceDocumentModel, PurchaseExpenseDocumentModel, PurchaseVatTableItemModel, VatTableItemModel } from '@skylab-shared/dataModel';
import { Calc, dateDiffInDays, head, MidpointRounding, substractDays, sum, withFlag } from '@skylab-shared/utils';
import { PurchaseDocumentJpkGroupsBOPart } from './parts/purchase-document-jpk-groups.bo-part';
import { PurchaseDocumentPaymentsBOPart } from './parts/purchase-document-payments.bo-part';
import { PurchaseDocumentValidationBOPart } from './parts/purchase-document-validation.bo-part';
import { PurchaseDocumentVatDeductionDateBOPart } from './parts/purchase-document-vat-deduction-date.bo-part';
import { PurchaseExpenseDocumentValidationBOPart } from './parts/purchase-expense-document-validation.bo-part';
import { PurchaseKsefNumberAlreadyInUseQuery } from './queries/purchase-ksef-number-already-in-use.query';
import { rewriteAddressFromParsedEInvoiceAddress } from './utils/purchase-rewriting-utils';
import { PurchaseKsefNumberAlreadyInUseError } from './violations/purchase-ksef-number-already-in-use-error';
import { PurchaseKsefNumberInvalidError } from './violations/purchase-ksef-number-invalid-error';

export interface PurchaseExpenseDocumentBO extends ExchangeRateMixin<PurchaseExpenseDocumentModel>, ToPayMixin<PurchaseExpenseDocumentModel> {};

@BO({
    modelType: PurchaseExpenseDocumentModel,
    mixins: [ExchangeRateMixin, ToPayMixin]
})
export class PurchaseExpenseDocumentBO extends BusinessObjectBase<PurchaseExpenseDocumentModel> {
    private readonly vatRateNetCalculator: VatTableItemNetRecalculation;
    private readonly vatRateGrossCalculator: VatTableItemGrossRecalculation;

    public constructor(
        weaver: Weaver,
        protected readonly queryBus: QueryBus,
        private readonly clientRepository: ClientRepositoryBase
    ) {
        super(weaver);
        this.parts.push(new PurchaseDocumentValidationBOPart(weaver, queryBus));
        this.parts.push(new PurchaseExpenseDocumentValidationBOPart(weaver, queryBus));
        this.parts.push(new DocumentNumberBoPart(weaver, queryBus));
        this.parts.push(new DocumentExchangeRateValidationBoPart(weaver));
        this.parts.push(new PurchaseDocumentPaymentsBOPart(weaver));
        this.parts.push(new PurchaseDocumentJpkGroupsBOPart(weaver));
        this.parts.push(new PurchaseDocumentVatDeductionDateBOPart(weaver));

        this.vatRateNetCalculator = new VatTableItemNetRecalculation();
        this.vatRateGrossCalculator = new VatTableItemGrossRecalculation();
    }

    protected override async initializeAsync(): Promise<void> {
        await super.initializeAsync();

        await Promise.all([
            this.localContext.preload(VatRateForPurchaseQuery),
            this.localContext.preload(CurrencyCollectionQuery),
            this.localContext.preload(CompanyParametersQuery),
            this.localContext.preload(PurchaseKindQuery),
            this.localContext.preload(CommunityTransactionForPurchaseQuery),
            this.localContext.preload(PaymentMethodQuery),
            this.localContext.preload(NumberingQuery)
        ]);

        this.registerCoreFeatures();
        this.registerCurrencyReadonlyRule();
        this.registerSetCurrencyOnCommunityTransactionChange();
        this.registerDocumentFromEInvoiceRules();
    }

    private documentRecalculation(): string {
        return `${this.model.sun}#document-recalculation`;
    }

    private documentItemRecalculation(documentItem: PurchaseVatTableItemModel): string {
        return `${documentItem.sun}#document-item-recalculation`;
    }

    private registerDocumentFromEInvoiceRules(): void {
        const modelType = PurchaseExpenseDocumentModel;
        const omitValidateKsefNumber = () => !this.model.ksefDocumentId || this.model.ksefHeaderId || !!this.internalRelatedEInvoiceDocument
        const ksefNumberValid = () => KsefNumberValidator.isValid(this.model.ksefDocumentId!);

        this.validation.addRule(
            [[modelType, 'ksefDocumentId']],
            _ => {
                if (omitValidateKsefNumber()) return true;
                return ksefNumberValid();
            },
            PurchaseKsefNumberInvalidError
        );

        this.validation.addRule(
            [[modelType, 'ksefDocumentId']],
            async _ => {
                if (omitValidateKsefNumber() || !ksefNumberValid()) return true;
                const result = await this.queryBus.queryAsync(new PurchaseKsefNumberAlreadyInUseQuery({ksefDocumentId: this.model.ksefDocumentId!}));
                if (!result.documentId) return true;
                if (this.model.isPersistent && (this.model.id === result.documentId)) return true;

                return !result.documentId;
            },
            PurchaseKsefNumberAlreadyInUseError
        );

        this.readonly.addRule([
            [modelType, 'ksefDocumentId'],
            [modelType, 'ksefCollectiveIdentifier'],
            [modelType, 'ksefSubmissionMode'],
        ], () => !!this.internalRelatedEInvoiceDocument || !!this.model.ksefHeaderId);
    }

    private registerCurrencyReadonlyRule(): void {
        const communityTransactions = this.localContext.getPreloaded(CommunityTransactionForPurchaseQuery);

        this.readonly.addRule(
            [[PurchaseExpenseDocumentModel, 'currencyId'], [PurchaseExpenseDocumentModel, 'communityTransactionId']],
            [[PurchaseExpenseDocumentModel, 'currencyId']],
            () => this.model.issuedByVatPayer ? this.model.communityTransactionId === communityTransactions.find(elem => elem.symbol === 'ZK')?.id : false
        );
    }

    private registerSetCurrencyOnCommunityTransactionChange(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(PurchaseExpenseDocumentModel, ['communityTransactionId'])(
            ctx => {
                const defaultCommunityTransaction = this.localContext.firstPreloaded(CommunityTransactionForPurchaseQuery, { default: true });
                if (this.model.communityTransactionId !== defaultCommunityTransaction.id) return;
                const { systemCurrency } = this.localContext.getPreloaded(CompanyParametersQuery);
                const pln = this.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency });
                this.model.currencyId = pln.id;
            }
        )));
    }

    public registerCoreFeatures(): void {
        this.propertyChanged.listen<PurchaseVatTableItemModel>(propertyChangedFilter(PurchaseVatTableItemModel, ['manual'], ctx => {
            withFlag(() => {
                const item = this.model.vatTable.find(item => item.vatRateId === ctx.model.vatRateId)!;
                if (item) item.manual = ctx.model.manual;
                if (!item.manual) {
                    if (this.inForeignCurrency() && this.exchangeRateSet) {
                        const documentCurrencyValues = this.calculateItem(item, 'net', VatTableItemContext.DocumentCurrency);
                        this.rewriteVatTableItemValuesToModel(item, documentCurrencyValues, VatTableItemContext.DocumentCurrency);
                        const systemCurrencyItem = this.calculateSystemCurrencyItemFromDocumentCurrencyItem(item);
                        this.rewriteVatTableItemValuesToModel(item, systemCurrencyItem, VatTableItemContext.SystemCurrency);
                    } else {
                        const systemCurrencyItem = this.calculateItem(item, this.model.calculationMethod === CalculationMethod.Net ? 'net' : 'gross', VatTableItemContext.SystemCurrency);
                        this.rewriteVatTableItemValuesToModel(item, systemCurrencyItem, VatTableItemContext.SystemCurrency);
                    }
                }
            }, 'edited-manually-flag-synchronization');
        }));

        this.propertyChanged.listen<PurchaseVatTableItemModel>(propertyChangedFilter(PurchaseVatTableItemModel, ['netValue', 'grossValue', 'currencyNetValue', 'vatValue'], ctx => {
            withFlag(() => {
                if (ctx.model.manual) {
                    if ((ctx.propertyName === 'vatValue' || ctx.propertyName === 'grossValue') && this.inForeignCurrency() && this.model.exchangeRate) {
                        const { precision } = this.localContext.firstPreloaded(CurrencyCollectionQuery, { id: this.model.currencyId });
                        ctx.model.currencyVatValue = Calc.round((ctx.model.vatValue / this.model.exchangeRate.rate) * this.model.exchangeRate.units, precision, MidpointRounding.AwayFromZero);
                        ctx.model.currencyGrossValue = Calc.round((ctx.model.grossValue / this.model.exchangeRate.rate) * this.model.exchangeRate.units, precision, MidpointRounding.AwayFromZero);
                    }
                    this.recalculateGrossValue();
                    return;
                }

                if (ctx.propertyName !== 'currencyNetValue') {
                    const itemToCalculate = this.model.vatTable.find(item => item.vatRateId === ctx.model.vatRateId)!;
                    const calculatedItem = this.calculateItem(ctx.model, ctx.propertyName as VatTableItemRecalculationChangesProperty, VatTableItemContext.SystemCurrency);
                    this.rewriteVatTableItemValuesToModel(itemToCalculate, calculatedItem, VatTableItemContext.SystemCurrency);
                } else {
                    if (this.inForeignCurrency() && this.exchangeRateSet) {
                        const itemToCalculate = this.model.vatTable.find(item => item.vatRateId === ctx.model.vatRateId)!;
                        const calculatedItem = this.calculateItem(ctx.model, ctx.propertyName, VatTableItemContext.DocumentCurrency);
                        this.rewriteVatTableItemValuesToModel(itemToCalculate, calculatedItem, VatTableItemContext.DocumentCurrency);
                        const systemCurrencyCalculatedItem = this.calculateSystemCurrencyItemFromDocumentCurrencyItem(itemToCalculate);
                        this.rewriteVatTableItemValuesToModel(itemToCalculate, systemCurrencyCalculatedItem, VatTableItemContext.SystemCurrency);
                    }
                }

                this.recalculateGrossValue();

            }, this.documentItemRecalculation(ctx.model));
        }));

        this.propertyChanged.listen<ExchangeRateModel>(propertyChangedFilter(ExchangeRateModel, ['units', 'rate'], () => {
            if (this.model.exchangeRate?.manual) {
                this.calculateDocumentCurrencyItemsFromSystemCurrencyItems();
            }
        }));

        this.registerListening(this.weaver.register(CollectionChangedJoinPoint, collectionChangedJoinPointFilter(this)(this.model.vatTable)(
            ctx => {
                if (ctx.action !== CollectionChangingAction.Remove) return;
                this.recalculateGrossValue();
            }
        )));
    }

    public recalculateGrossValue(): void {
        const { systemCurrency } = this.localContext.getPreloaded(CompanyParametersQuery);
        const baseCurrency = this.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency });

        this.model.grossValue = Calc.round(sum(this.model.vatTable.asArray(), i => i.grossValue), baseCurrency.precision, MidpointRounding.AwayFromZero);
    }

    private rewriteVatTableItemValuesToModel(vatTableItemModel: PurchaseVatTableItemModel, item: VatTableItem, rewriteDocumentCurrencyValues: VatTableItemContext) {
        switch (rewriteDocumentCurrencyValues) {
            case VatTableItemContext.DocumentCurrency:
                vatTableItemModel.currencyNetValue = item.netValue;
                vatTableItemModel.currencyGrossValue = item.grossValue;
                vatTableItemModel.currencyVatValue = item.vatValue;
                break;
            case VatTableItemContext.SystemCurrency:
                vatTableItemModel.grossValue = item.grossValue!;
                vatTableItemModel.netValue = item.netValue!;
                vatTableItemModel.vatValue = item.vatValue!;
                break;
        }
    }

    private convertVatTableItemModelToItem(vatTableItemModel: PurchaseVatTableItemModel, rate: number, itemContext: VatTableItemContext): VatTableItem {
        const convertedItem: VatTableItem = {
            vatRate: rate,
            vatRateId: vatTableItemModel.vatRateId,
            vatValue: itemContext == VatTableItemContext.DocumentCurrency ? vatTableItemModel.currencyVatValue : vatTableItemModel.vatValue,
            netValue: itemContext == VatTableItemContext.DocumentCurrency ? vatTableItemModel.currencyNetValue : vatTableItemModel.netValue,
            grossValue: itemContext == VatTableItemContext.DocumentCurrency ? vatTableItemModel.currencyGrossValue : vatTableItemModel.grossValue,
        };
        return convertedItem;
    }

    private calculateItem(item: PurchaseVatTableItemModel, updatedProperty: VatTableItemRecalculationChangesProperty, itemContext: VatTableItemContext): VatTableItem {
        const { precision } = this.localContext.firstPreloaded(CurrencyCollectionQuery, { id: this.model.currencyId });
        const { rate } = this.localContext.firstPreloaded(VatRateForPurchaseQuery, { id: item.vatRateId });
        const convertedItem = this.convertVatTableItemModelToItem(item, rate, itemContext);

        switch (itemContext) {
            case VatTableItemContext.DocumentCurrency:
                return this.vatRateNetCalculator.recalculate(convertedItem, precision, 'net');
            case VatTableItemContext.SystemCurrency:
                return this.model.calculationMethod == CalculationMethod.Net
                    ? this.vatRateNetCalculator.recalculate(convertedItem, precision, updatedProperty)
                    : this.vatRateGrossCalculator.recalculate(convertedItem, precision, updatedProperty);
        }
    }

    public calculateDocumentCurrencyItemsFromSystemCurrencyItems() {
        if (this.inForeignCurrency()) {
            this.model.vatTable
                .forEach(item => {
                    if (!this.model.exchangeRate) return;

                    if (item.manual) {
                        const { precision } = this.localContext.firstPreloaded(CurrencyCollectionQuery, { id: this.model.currencyId });
                        item.currencyVatValue = Calc.round((item.vatValue / this.model.exchangeRate.rate) * this.model.exchangeRate.units, precision, MidpointRounding.AwayFromZero);
                        item.currencyGrossValue = Calc.round((item.grossValue / this.model.exchangeRate.rate) * this.model.exchangeRate.units, precision, MidpointRounding.AwayFromZero);
                    } else {
                        const { precision } = this.localContext.firstPreloaded(CurrencyCollectionQuery, { id: this.model.currencyId });
                        const multiplier = this.model.exchangeRate.rate / (this.model.exchangeRate.units == 0 ? 1 : this.model.exchangeRate.units);
                        item.netValue = Calc.round(item.currencyNetValue! * multiplier, precision, MidpointRounding.AwayFromZero);
                    }
                });
        }
    }

    private calculateSystemCurrencyItemFromDocumentCurrencyItem(item: PurchaseVatTableItemModel): VatTableItem {
        const { precision } = this.localContext.firstPreloaded(CurrencyCollectionQuery, { id: this.model.currencyId });
        const { rate } = this.localContext.firstPreloaded(VatRateForPurchaseQuery, { id: item.vatRateId });
        const multiplier = this.model.exchangeRate ? this.model.exchangeRate.rate / (this.model.exchangeRate.units == 0 ? 1 : this.model.exchangeRate.units) : 1;
        const convertedItem: VatTableItem = {
            vatRate: rate,
            vatRateId: item.vatRateId,
            vatValue: item.currencyVatValue,
            netValue: Calc.round(item.currencyNetValue! * multiplier, precision, MidpointRounding.AwayFromZero),
            grossValue: Calc.round(item.currencyGrossValue! * multiplier, precision, MidpointRounding.AwayFromZero)
        };
        return this.vatRateNetCalculator.recalculate(convertedItem, precision, 'net');
    }

    public calculateForeignCurrencyItemsNetValue(): void {
        this.model.vatTable
            .filter(({ manual }) => !manual)
            .forEach(item => {
                if (this.inForeignCurrency()) {
                    this.updateVatTableItemValues(item);
                }
            });
    }

    private updateVatTableItemValues(item: PurchaseVatTableItemModel): void {
        const multiplier = this.model.exchangeRate ? this.model.exchangeRate.rate / (this.model.exchangeRate.units == 0 ? 1 : this.model.exchangeRate.units) : 1;
        const { systemCurrency } = this.localContext.getPreloaded(CompanyParametersQuery);
        const { precision } = this.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency });
        item.currencyNetValue = Calc.round(item.netValue / multiplier, precision, MidpointRounding.AwayFromZero);
    }

    private get exchangeRateSet(): boolean {
        return this.model.exchangeRate !== null;
    }

    protected calculateDateForCurrencyExchangeRate(): Date {
        const date = substractDays(this.model.vatDeductionDate || this.model.receiptDate, 1);
        return BusinessDayCalculatorPL.getLastBusinessDay(date);
    }

    public async fillDocumentToEPurchaseInvoice(source: PurchaseEInvoiceDocumentModel, sellerId: number | null): Promise<void> {
        this.setInternalRelatedEInvoiceDocument(source);
        const { systemCurrency } = this.localContext.getPreloaded(CompanyParametersQuery);
        const systemCurrencyId = this.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency }).id;
        const purchaseDocumentDefaults = this.localContext.firstPreloaded(PurchaseKindQuery, {name: PurchaseDocumentKind.Expense});

        const numberings = this.localContext.getPreloaded(NumberingQuery);
        const paymentMethods = this.localContext.getPreloaded(PaymentMethodQuery);

        this.model.originalEInvoiceDocumentId = source.id;
        this.model.ksefHeaderId = source.ksefHeaderId;
        this.model.ksefHash = source.ksefHash;
        this.model.ksefCollectiveIdentifier = source.ksefCollectiveIdentifier;
        this.model.ksefDocumentId = source.ksefDocumentId;
        this.model.ksefSubmissionMode = source.ksefSubmissionMode;
        this.model.dateOfAssigningKsefNumber = source.dateOfAssigningKsefNumber;
        this.model.issuedByVatPayer = source.issuedByVatPayer;

        if (!sellerId) {
            await rewriteDocumentContractorWithFreshDataAsync(this.model, source, this.clientRepository);
            rewriteAddressFromParsedEInvoiceAddress(this.model.seller?.address, source.seller?.address);
        } else {
            await fillDocumentContractorBasedOnContractorId(this.model, this.clientRepository, sellerId);
        }

        this.model.calculationMethod = ([CalculationMethod.Gross, CalculationMethod.Net].includes(source.calculationMethod as CalculationMethod.Gross | CalculationMethod.Net)
            ? source.calculationMethod
            : source.issuedByVatPayer
                ? purchaseDocumentDefaults.defaultCalculationMethod
                : CalculationMethod.Gross) as CalculationMethod.Gross | CalculationMethod.Net;

        this.model.cashBasis = source.cashBasis;
        this.model.fixedAsset = source.fixedAsset;

        this.model.communityTransactionId = source.communityTransactionId
        this.model.currencyId = source.currencyId;
        this.model.bankAccount = source.bankAccount;

        source.payments.forEach(sourcePayment => {
            const model = new DocumentPaymentListItemModel();
            model.date = sourcePayment.date;
            model.onDocument = sourcePayment.onDocument;
            model.value = sourcePayment.value;
            this.model.payments.push(model);
        });
        this.model.externalDocumentNumber = source.externalDocumentNumber;
        const [{ id: defaultNumberingId }] = numberings.filter(n => n.default && n.kinds.includes(this.model.kind));
        this.model.numberingId = defaultNumberingId;

        this.model.deliveryDate = source.deliveryDate || source.issueDate;
        this.model.issueDate = source.issueDate;
        this.model.receiptDate = source.receiptDate;
        this.model.vatDeductionDate = source.vatDeductionDate;

        this.model.jpkVatGroups = source.jpkVatGroups;
        this.model.splitPayment = source.splitPayment;

        if (paymentMethods.find(e => e.id === source.paymentMethodId)) {
            this.model.paymentMethodId = source.paymentMethodId;
        }

        this.model.dueDate = source.dueDate || head(this.model.payments?.asArray() || [])?.date || this.model.issueDate;
        this.model.dueDateDays = this.model.issueDate ? dateDiffInDays(this.model.dueDate, this.model.issueDate) : null;

        this.model.note = source.note;

        if (source.exchangeRate) {
            const exchangeRate = new ExchangeRateModel();
            this.model.exchangeRate = exchangeRate;
            rewriteExchangeRate(exchangeRate, source.exchangeRate);
        }

        this.model.grossValue = source.grossValue;

        this.model.vatTable.push(...(source.vatTableItems || []).map((sourceVatItem: VatTableItemModel) => {
            const item = new PurchaseVatTableItemModel();
            item.manual = true;
            item.vatRateId = sourceVatItem.vatRateId;
            if (this.model.currencyId !== systemCurrencyId) {
                item.currencyNetValue = sourceVatItem.netValue;
                item.currencyGrossValue = sourceVatItem.grossValue;
                item.currencyVatValue = sourceVatItem.vatValue;
            } else {
                item.grossValue = sourceVatItem.grossValue;
                item.netValue = sourceVatItem.netValue;
                item.vatValue = sourceVatItem.vatValue;
            }
            return item;
        }));
    }

    private internalRelatedEInvoiceDocument: PurchaseEInvoiceDocumentModel;
    private async setInternalRelatedEInvoiceDocument(value: PurchaseEInvoiceDocumentModel) {
        this.internalRelatedEInvoiceDocument = value;
    }

    public getRelatedEInvoiceDocument(): PurchaseEInvoiceDocumentModel {
        return this.internalRelatedEInvoiceDocument;
    }
}
